// Chapter 5 - Improving and Assessing the Quality of Behavioral Measurement
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 5.
// All content traced to source-pdfs/Chapter-5-Improving-and-Assessing-the-Quality-of-Behavioral-Measurement-4.pdf

import type { ChapterModule } from "./types";

const chapter05Module: ChapterModule = {
  meta: {
    id: "05",
    number: 5,
    title: "Improving and Assessing the Quality of Behavioral Measurement",
    subtitle: "Validity, accuracy, reliability, IOA",
    part: "Part 2 - Selecting, Defining, and Measuring Behavior",
    estMinutes: 80,
    bacb: ["C-8", "C-9"],
  },
  curated: true,
  overview: [
    "Chapter 5 raises the bar from 'can we measure it?' to 'should anyone believe these numbers?' It defines the three pillars of trustworthy measurement (validity, accuracy, reliability), surfaces the major threats (indirect measurement, observer drift, observer expectations, observer reactivity, measurement bias, measurement artifact), and shows how to assess data quality using accuracy checks, reliability checks, and interobserver agreement (IOA).",
    "You will learn the difference between observed and true values, why IOA is necessary but not sufficient for accuracy, and how to choose among the IOA formulas for event, duration, latency, IRT, trial-by-trial, and interval data. The chapter closes with conventions for reporting IOA (>=80% minimum, distributed across conditions, days, times, settings, and observers).",
  ],
  objectives: [
    { id: "C-8", text: "Evaluate the validity and reliability of measurement procedures." },
    { id: "C-9", text: "Select a measurement system to obtain representative data given the dimensions of behavior and the logistics of observing and recording." },
  ],
  coreConcepts: [
    {
      title: "Validity, accuracy, reliability: three pillars of trustworthy data",
      body: [
        "To be useful for science, measurement must be valid (measuring the right thing), accurate (observed values match true values), and reliable (repeated measurement yields the same values). They are independent: data can be reliably wrong, accurate on average but not reliable, or valid in form but inaccurate in execution.",
      ],
      bullets: [
        "Validity = right behavior + right dimension + representative time/conditions.",
        "Accuracy = correspondence between observed and true values.",
        "Reliability = consistency across repeated measurements of the same event.",
        "High reliability does NOT confirm high accuracy; low reliability disqualifies the data until fixed.",
      ],
    },
    {
      title: "Threats to validity",
      body: [
        "Validity is compromised when measurement is indirect (something other than the behavior of interest), when the dimension measured is irrelevant to the question, or when discontinuous/poorly scheduled observations and insensitive scales produce measurement artifact.",
      ],
      bullets: [
        "Indirect measurement requires inference from a proxy to the actual behavior.",
        "Wrong dimension: counting outbursts when duration is the clinically relevant question.",
        "Measurement artifact: data that misrepresent the behavior due to how measurement was conducted.",
        "Discontinuous measurement is a common source of artifact when occurrences are spread thinly across time.",
      ],
    },
    {
      title: "Threats to accuracy and reliability (observer error)",
      body: [
        "Human observers are the dominant source of measurement error. Poorly designed systems, weak training, observer drift, observer expectations, observer reactivity, and measurement bias all degrade accuracy and reliability.",
      ],
      bullets: [
        "Observer drift: unintended changes in how an observer applies the measurement system over time.",
        "Expectations / hypothesis-confirming bias: knowing the predicted result skews coding.",
        "Observer reactivity: observers behave differently when they know their data are being evaluated.",
        "Measurement bias: systematic error (over- or under-counting) introduced by expectations or design.",
        "Naive observers (blind to conditions/hypotheses) mitigate bias.",
      ],
    },
    {
      title: "Observed value vs true value",
      body: [
        "An observed value is the datum produced by measurement. A true value is what the measurement should yield if executed perfectly. Some true values are easy (clock time, counted items); others require a contrived permanent product or video record so observers can remeasure the same event.",
      ],
    },
    {
      title: "Accuracy assessment",
      body: [
        "Assessing accuracy means calculating the correspondence between each datum and its true value. Accuracy checks (a) reveal whether data are usable, (b) catch and correct measurement errors, (c) detect patterns of error that can lead to calibration improvements, and (d) communicate trustworthiness to others.",
      ],
    },
    {
      title: "Reliability assessment",
      body: [
        "Assessing reliability requires a natural or contrived permanent product (paper, video, audio) so the observer can remeasure the same behavioral events. Reliability does NOT confirm accuracy; it confirms that the same observer (or another) reaches the same numbers across repeated coding.",
      ],
    },
    {
      title: "Interobserver agreement (IOA): the workhorse",
      body: [
        "IOA is the degree to which two or more independent observers report the same observed values after measuring the same events. It is the most commonly used indicator of measurement quality in ABA. Uses: (a) competence-check new observers, (b) detect drift, (c) judge clarity of target definitions, (d) convince others of believability.",
      ],
      bullets: [
        "Two or more observers must use the same observation code and measurement system.",
        "They must observe and measure the same participants and events.",
        "They must record independently, without influence from each other.",
      ],
    },
    {
      title: "IOA formulas by data type",
      body: [
        "Different data types require different IOA formulas. Pick the most stringent formula appropriate to the data.",
      ],
      bullets: [
        "Total count IOA: smaller/larger total counts; simplest and crudest for event data.",
        "Mean count-per-interval IOA: average across smaller intervals; more sensitive than total count.",
        "Exact count-per-interval IOA: counts must match exactly per interval; most stringent for event data.",
        "Trial-by-trial IOA: agreement on each 1/0 trial; for discrete-trial data.",
        "Total duration IOA: shorter / longer total duration.",
        "Mean duration-per-occurrence IOA: per-occurrence comparison (preferred for duration data).",
        "Interval-by-interval IOA: agreement per interval; can overestimate with very low/high rates.",
        "Scored-interval IOA: agreement only on intervals scored YES; recommended for low-frequency behaviors.",
        "Unscored-interval IOA: agreement only on intervals scored NO; recommended for high-frequency behaviors.",
      ],
    },
    {
      title: "IOA conventions and reporting",
      body: [
        "Convention has been a minimum acceptable IOA of 80%, with the caveat that no fixed criterion fits every behavior. The nature of the behavior and the degree of behavior change must be considered. Report IOA at the same level you report results, distributed across conditions, phases, days, times, settings, and observers. Use multiple indices (accuracy + IOA, reliability + IOA) to triangulate trust.",
      ],
    },
  ],
  glossary: [
    { term: "Validity", definition: "Measurement encompassing (a) the right (socially significant) target behavior, (b) the right dimension, and (c) representativeness of time/conditions.", sourceHint: "Valid measurement in ABA encompasses three equally important elements." },
    { term: "Accuracy", definition: "The correspondence between observed values and the true values of the events being measured.", sourceHint: "Measurement is accurate when observed values, the data produced by measuring an event, match the true state, or true values, of the event." },
    { term: "Reliability", definition: "The consistency of measurement across repeated measurement of the same event.", sourceHint: "Measurement is reliable when it yields the same values across repeated measurement of the same event." },
    { term: "Believability", definition: "The trustworthiness of measurement, judged by validity, accuracy, reliability, and IOA.", sourceHint: "believability (Key Terms list)." },
    { term: "True value", definition: "The value that measurement would yield if executed perfectly.", sourceHint: "True values for many behaviors of interest to applied behavior analysts are evident and universally accepted or can be established conditionally by local context." },
    { term: "Observed value", definition: "The datum produced by a measurement procedure.", sourceHint: "observed value (Key Terms list)." },
    { term: "Direct measurement", definition: "Measurement of the behavior of interest itself.", sourceHint: "direct measurement (Key Terms list)." },
    { term: "Indirect measurement", definition: "Measurement of a behavior other than the one of interest, requiring inference.", sourceHint: "Indirect measurement - measuring a behavior different from the behavior of interest - threatens validity." },
    { term: "Continuous measurement", definition: "Measurement that detects every instance of behavior in the observation period.", sourceHint: "continuous measurement (Key Terms list)." },
    { term: "Discontinuous measurement", definition: "Measurement that samples rather than detecting every instance (e.g., time sampling).", sourceHint: "Discontinuous measurement, poorly scheduled observations, and insensitive or limiting measurement scales are common causes of measurement artifacts." },
    { term: "Measurement bias", definition: "Systematic over- or under-reporting caused by observer expectations or measurement design.", sourceHint: "Measurement bias caused by observer expectations can be avoided by using naive observers." },
    { term: "Observer drift", definition: "Unintended changes in the way an observer uses a measurement system over the course of an investigation.", sourceHint: "Observer drift - unintended changes in the way an observer uses a measurement system over the course of an investigation." },
    { term: "Observer reactivity", definition: "Measurement error caused by an observer's awareness that others are evaluating the data being reported.", sourceHint: "Observer reactivity is measurement error caused by an observer's awareness that others are evaluating the data he reports." },
    { term: "Naive observer", definition: "An observer who is blind to the experimental conditions or hypotheses; used to control bias.", sourceHint: "Measurement bias caused by observer expectations can be avoided by using naive observers." },
    { term: "Calibration", definition: "Adjusting a measurement system or observer to match a known standard.", sourceHint: "(consistent patterns of measurement error that) can lead to the overall improvement or calibration of the measurement system." },
    { term: "Interobserver agreement (IOA)", definition: "The degree to which two or more independent observers report the same observed values after measuring the same events.", sourceHint: "The most commonly used indicator of measurement quality in ABA is interobserver agreement (IOA), the degree to which two or more independent observers report the same observed values after measuring the same events." },
    { term: "Total count IOA", definition: "Smaller total / larger total for event data; simplest and crudest IOA formula.", sourceHint: "Total count IOA is the simplest and crudest indicator of IOA for event recording data." },
    { term: "Mean count-per-interval IOA", definition: "Average of per-interval IOA values across smaller time intervals within the observation period.", sourceHint: "the counts tallied by each observer during each of a series of smaller intervals of time within the measurement period." },
    { term: "Exact count-per-interval IOA", definition: "Counts must match exactly per interval; the most stringent IOA formula for event data.", sourceHint: "exact count-per-interval IOA is the most stringent for most data sets obtained by event recording." },
    { term: "Trial-by-trial IOA", definition: "Agreement on each 1/0 trial for discrete-trial data.", sourceHint: "each observer's count of 1 or 0 on a trial-by-trial basis." },
    { term: "Total duration IOA", definition: "Shorter total duration / longer total duration; coarse but acceptable for duration data.", sourceHint: "Total duration IOA is computed by dividing the shorter of the two durations reported by the observers by the longer duration." },
    { term: "Mean duration-per-occurrence IOA", definition: "Average of per-occurrence durations from each observer; more conservative than total duration IOA.", sourceHint: "Mean duration-per-occurrence IOA is a more conservative and usually more meaningful assessment of IOA for total duration data." },
    { term: "Interval-by-interval IOA", definition: "Agreement per interval; sensitive to chance agreement when behavior is very rare or very dense.", sourceHint: "Because it is subject to random or accidental agreement between observers, interval-by-interval IOA is likely to overestimate the degree of agreement." },
    { term: "Scored-interval IOA", definition: "Agreement only on intervals scored YES; preferred for low-frequency behaviors.", sourceHint: "Scored-interval IOA is recommended for behaviors that occur at relatively low frequencies." },
    { term: "Unscored-interval IOA", definition: "Agreement only on intervals scored NO; preferred for high-frequency behaviors.", sourceHint: "unscored-interval IOA is recommended for behaviors that occur at relatively high frequencies." },
  ],
  procedures: [
    {
      title: "Build a defensible measurement program",
      intro: "Front-load training, calibration, and trust-checks before relying on data.",
      steps: [
        { title: "Design the system", detail: "Pick definitions, dimensions, methods, intervals, and rules per Chapters 3 and 4." },
        { title: "Train observers systematically", detail: "Use written definitions, video exemplars, practice trials with feedback, until predetermined accuracy and reliability criteria are met." },
        { title: "Pilot and calibrate", detail: "Run two-observer pilots and adjust definitions or method; recalibrate against a true-value standard." },
        { title: "Plan ongoing IOA", detail: "Schedule IOA across conditions, days, times, settings, and observers; pick formula appropriate to data type." },
        { title: "Withhold result feedback", detail: "Do not tell observers whether their data confirm hypotheses; this preserves accuracy." },
      ],
    },
    {
      title: "Calculate IOA for event data",
      intro: "Pick the formula that matches stringency needs.",
      steps: [
        { title: "Total count IOA", detail: "smaller total count / larger total count x 100. Simplest, but can mask disagreement on individual instances." },
        { title: "Mean count-per-interval IOA", detail: "Compute IOA per interval, then average; more sensitive than total count." },
        { title: "Exact count-per-interval IOA", detail: "Count intervals with EXACT match / total intervals x 100. Most stringent." },
        { title: "Trial-by-trial IOA", detail: "Agreements (1=1 or 0=0) / total trials x 100. Standard for DTT data." },
      ],
    },
    {
      title: "Calculate IOA for interval data",
      intro: "Choose by behavior rate.",
      steps: [
        { title: "Interval-by-interval IOA", detail: "Intervals of agreement (both YES or both NO) / total intervals x 100. Can overestimate with very rare or very dense behavior." },
        { title: "Scored-interval IOA", detail: "Agreements on YES / (agreements on YES + disagreements) x 100. Use for low-frequency behaviors." },
        { title: "Unscored-interval IOA", detail: "Agreements on NO / (agreements on NO + disagreements) x 100. Use for high-frequency behaviors." },
      ],
    },
  ],
  examples: [
    { scenario: "Two coders both report 10 episodes in a session; one called episodes at minutes 1, 3, 5, 7, 9; the other at minutes 2, 4, 6, 8, 10.",
      application: "Total count IOA = 100%, but exact count-per-interval IOA may be 0% in any short interval - choose the more stringent formula.",
      sourceHint: "exact count-per-interval IOA is the most stringent for most data sets obtained by event recording." },
    { scenario: "Observers know a treatment is supposed to reduce tantrums and gradually start labeling shorter bursts as 'not really' a tantrum over the next two weeks.",
      application: "Observer drift compounded by expectation bias; respond with booster training, blinded reliability checks, and naive observers.",
      sourceHint: "Observer drift can be minimized by booster training sessions and feedback on the accuracy and reliability of measurement." },
    { scenario: "A researcher uses video so a second observer can rescore the same session a week later.",
      application: "Reliability check using a contrived permanent product (the video).",
      sourceHint: "Assessing the reliability of measurement requires a natural or contrived permanent product so the observer can remeasure the same behavioral events." },
    { scenario: "Behavior occurs 1 minute in a 30-minute session; interval-by-interval IOA is 97% but observers disagreed on the only YES interval.",
      application: "Interval-by-interval IOA overestimates agreement when behavior is rare. Use scored-interval IOA.",
      sourceHint: "interval-by-interval IOA is likely to overestimate the degree of agreement between observers measuring behaviors that occur at very low or very high rates." },
  ],
  comparisons: [
    {
      title: "Validity vs Accuracy vs Reliability",
      left:  { label: "Validity", points: [
        "Are we measuring the right behavior, dimension, and times?",
        "Validity is about the relevance of the measure.",
      ]},
      right: { label: "Accuracy vs Reliability", points: [
        "Accuracy: observed values match true values.",
        "Reliability: same numbers across repeated measurement of the same event.",
        "High reliability does NOT prove high accuracy.",
      ]},
    },
    {
      title: "Direct vs Indirect measurement",
      left:  { label: "Direct", points: [
        "Measures the actual behavior of interest.",
        "Higher validity by default.",
      ]},
      right: { label: "Indirect", points: [
        "Measures something different from the behavior of interest.",
        "Requires evidence that the proxy reflects the target.",
      ]},
    },
    {
      title: "Scored-interval vs Unscored-interval IOA",
      left:  { label: "Scored-interval", points: [
        "Agreement only on intervals scored YES.",
        "Use for LOW-frequency behaviors.",
      ]},
      right: { label: "Unscored-interval", points: [
        "Agreement only on intervals scored NO.",
        "Use for HIGH-frequency behaviors.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "If observers agree, the data must be accurate.",
      reality: "IOA can be high even when both observers are systematically wrong. Accuracy requires comparison to a true value, not just agreement.",
      sourceHint: "Although high reliability does not confirm high accuracy, discovering a low level of reliability signals that the data are suspect.",
    },
    {
      myth: "80% IOA is always acceptable.",
      reality: "80% is a convention, not a law. The nature of the behavior and the size of the effect determine what counts as acceptable.",
      sourceHint: "The convention for acceptable IOA has been a minimum of 80%, but there can be no set criterion. The nature of the behavior being measured and the degree of behavior change revealed by the data must be considered.",
    },
    {
      myth: "Telling observers when they are right boosts accuracy.",
      reality: "Observers should NOT receive feedback about whether their data confirm hypotheses; that introduces expectation bias.",
      sourceHint: "Observers should not receive feedback about the extent to which their data confirm or run counter to hypothesized results or treatment goals.",
    },
    {
      myth: "Total count IOA is the gold standard.",
      reality: "Total count IOA is the simplest and crudest formula. Use exact count-per-interval or mean count-per-interval when the data permit.",
      sourceHint: "Total count IOA is the simplest and crudest indicator of IOA for event recording data, and exact count-per-interval IOA is the most stringent.",
    },
    {
      myth: "Interval-by-interval IOA is always conservative.",
      reality: "Interval-by-interval IOA can overestimate agreement when behavior is very rare or very dense due to accidental agreement on the majority interval type.",
      sourceHint: "Because it is subject to random or accidental agreement between observers, interval-by-interval IOA is likely to overestimate the degree of agreement.",
    },
  ],
  examChecklist: [
    "Define validity, accuracy, and reliability and explain their independence.",
    "List four common threats to measurement validity.",
    "Define observer drift, observer expectations, observer reactivity, and measurement bias.",
    "Explain why naive observers protect against bias.",
    "Define IOA and list its four primary uses.",
    "State the three conditions IOA requires.",
    "Pick an IOA formula for event data and explain the trade-off.",
    "Choose scored-interval vs unscored-interval IOA by behavior rate.",
    "Explain why high reliability does not confirm high accuracy.",
    "State the convention for minimum acceptable IOA and the caveat.",
  ],
  flashcardTerms: [
    "Validity", "Accuracy", "Reliability", "Believability",
    "True value", "Observed value", "Direct measurement", "Indirect measurement",
    "Continuous measurement", "Discontinuous measurement",
    "Measurement bias", "Observer drift", "Observer reactivity", "Naive observer",
    "Calibration",
    "Interobserver agreement (IOA)",
    "Total count IOA", "Mean count-per-interval IOA", "Exact count-per-interval IOA",
    "Trial-by-trial IOA",
    "Total duration IOA", "Mean duration-per-occurrence IOA",
    "Interval-by-interval IOA", "Scored-interval IOA", "Unscored-interval IOA",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which of the following is NOT one of the three pillars of trustworthy measurement?",
      options: [
        { label: "A", text: "Validity" },
        { label: "B", text: "Accuracy" },
        { label: "C", text: "Reliability" },
        { label: "D", text: "Replicability" },
      ],
      answer: "D",
      explanation: "The three pillars are validity, accuracy, and reliability. Replicability is an experimental-design virtue but not one of the three pillars of measurement quality.",
      topic: "Pillars of measurement",
      sourceHint: "To be most useful for science, measurement must be valid, accurate, and reliable.",
    },
    {
      id: "Q2",
      question: "Observers code shorter and shorter durations as tantrums over the course of a study because they 'know' the treatment is working. Best label:",
      options: [
        { label: "A", text: "Observer reactivity" },
        { label: "B", text: "Observer drift / expectation bias" },
        { label: "C", text: "Measurement artifact" },
        { label: "D", text: "Naive observer effect" },
      ],
      answer: "B",
      explanation: "Observer drift is unintended change in how observers apply the system; here it is compounded by expectation bias. Booster training and naive observers mitigate it.",
      topic: "Threats to accuracy",
      sourceHint: "Observer drift - unintended changes in the way an observer uses a measurement system over the course of an investigation - can be minimized by booster training sessions and feedback.",
    },
    {
      id: "Q3",
      question: "Which IOA formula is recommended for high-frequency behaviors measured with interval recording?",
      options: [
        { label: "A", text: "Interval-by-interval IOA" },
        { label: "B", text: "Scored-interval IOA" },
        { label: "C", text: "Unscored-interval IOA" },
        { label: "D", text: "Total count IOA" },
      ],
      answer: "C",
      explanation: "Unscored-interval IOA looks only at intervals where neither observer scored the behavior; it is the right choice when behavior is dense.",
      topic: "IOA formulas",
      sourceHint: "unscored-interval IOA is recommended for behaviors that occur at relatively high frequencies.",
    },
    {
      id: "Q4",
      question: "Which best describes the relationship between reliability and accuracy?",
      options: [
        { label: "A", text: "High reliability guarantees high accuracy." },
        { label: "B", text: "High accuracy guarantees high reliability." },
        { label: "C", text: "High reliability does not confirm high accuracy, but low reliability signals untrustworthy data." },
        { label: "D", text: "Accuracy and reliability are the same thing." },
      ],
      answer: "C",
      explanation: "Observers can reliably reach the wrong number; reliability speaks to consistency, not correctness. Low reliability does flag data as suspect.",
      topic: "Reliability vs accuracy",
      sourceHint: "Although high reliability does not confirm high accuracy, discovering a low level of reliability signals that the data are suspect enough to be disregarded.",
    },
    {
      id: "Q5",
      question: "Which IOA formula is the most STRINGENT for event recording data?",
      options: [
        { label: "A", text: "Total count IOA" },
        { label: "B", text: "Mean count-per-interval IOA" },
        { label: "C", text: "Exact count-per-interval IOA" },
        { label: "D", text: "Trial-by-trial IOA" },
      ],
      answer: "C",
      explanation: "Exact count-per-interval requires each interval's counts to match exactly; nothing weaker fully captures small disagreements.",
      topic: "IOA formulas",
      sourceHint: "exact count-per-interval IOA is the most stringent for most data sets obtained by event recording.",
    },
    {
      id: "Q6",
      question: "Why should observers be 'naive' to experimental conditions where possible?",
      options: [
        { label: "A", text: "To reduce observer fatigue." },
        { label: "B", text: "To save training time." },
        { label: "C", text: "To control measurement bias caused by observer expectations." },
        { label: "D", text: "To increase reactivity." },
      ],
      answer: "C",
      explanation: "Naive observers (blind to phase/hypothesis) reduce the systematic over- or under-counting that expectations otherwise create.",
      topic: "Measurement bias",
      sourceHint: "Measurement bias caused by observer expectations can be avoided by using naive observers.",
    },
    {
      id: "Q7",
      question: "Assessing reliability requires:",
      options: [
        { label: "A", text: "Two live observers in the same room." },
        { label: "B", text: "A permanent product (natural or contrived) so observers can remeasure the SAME events." },
        { label: "C", text: "An interval-by-interval scoring sheet." },
        { label: "D", text: "Knowledge of the experimental hypothesis." },
      ],
      answer: "B",
      explanation: "Reliability is consistency across REPEATED measurement of the same event; that requires a permanent product (video, audio, paper) so the event can be re-coded.",
      topic: "Reliability",
      sourceHint: "Assessing the reliability of measurement requires a natural or contrived permanent product so the observer can remeasure the same behavioral events.",
    },
  ],
  reviewPrompts: [
    "Walk through a recent measurement system and identify one threat each to validity, accuracy, and reliability.",
    "Sketch the IOA you would report for a study that uses partial-interval recording on a high-rate behavior.",
    "Design observer training (including criteria) that prevents drift in a 6-month study.",
    "Explain why 'observers know which condition is in effect' is a methodological problem and how to address it.",
    "Defend or critique an 80% IOA threshold for a tiny behavior change.",
    "Describe a case where direct measurement would not be feasible and how indirect measurement could be justified.",
  ],
  recap: [
    "Trustworthy measurement rests on validity (the right behavior, dimension, and conditions), accuracy (observed matches true), and reliability (consistent on remeasurement). They are independent. Reliability does not imply accuracy.",
    "Human observers are the dominant source of error. Drift, expectations, reactivity, and bias all degrade quality. Systematic training, naive observers, withheld result feedback, and booster sessions counter them.",
    "IOA is the workhorse of measurement quality in ABA. Pick the most stringent formula your data type permits, schedule IOA across conditions/days/observers, report at the same level as your results, and apply 80% as a convention rather than a law.",
  ],
  sourceExcerpts: [
    {
      heading: "Three pillars (Summary §1-4)",
      text: "To be most useful for science, measurement must be valid, accurate, and reliable. Valid measurement in ABA encompasses three equally important elements: (a) measuring directly a socially significant target behavior, (b) measuring a dimension of the target behavior relevant to the question or concern about the behavior, and (c) ensuring that the data are representative of the behavior under conditions and during times most relevant to the reason(s) for measuring it.",
    },
    {
      heading: "Observer drift",
      text: "Observer drift - unintended changes in the way an observer uses a measurement system over the course of an investigation - can be minimized by booster training sessions and feedback on the accuracy and reliability of measurement. An observer's expectations or knowledge about predicted or desired results can impair the accuracy and reliability of data.",
    },
    {
      heading: "IOA conventions",
      text: "The convention for acceptable IOA has been a minimum of 80%, but there can be no set criterion. The nature of the behavior being measured and the degree of behavior change revealed by the data must be considered when determining an acceptable level of IOA. IOA assessments should occur during each condition and phase of a study and be distributed across days of the week, times of day, settings, and observers.",
    },
    {
      heading: "Interval IOA formulas",
      text: "Three techniques commonly used to calculate IOA for interval data are interval-by-interval IOA, scored-interval IOA, and unscored-interval IOA. Scored-interval IOA is recommended for behaviors that occur at relatively low frequencies; unscored-interval IOA is recommended for behaviors that occur at relatively high frequencies.",
    },
  ],
};

export default chapter05Module;
